// src/Components/Broadcast/hooks/useProducts.js
import { useState, useCallback, useEffect } from 'react';
import { collection, getDocs, query, where, limit } from "firebase/firestore";
import { db } from '../../../firebase/firestore';
import { productsCache, giftsCache } from '../services/cacheService';

export const useProducts = (showNotification) => {
  const [products, setProducts] = useState({ courses: [], trainings: [] });
  const [productFilter, setProductFilter] = useState({ courses: [], trainings: [] });
  const [gifts, setGifts] = useState([]);
  const [giftFilter, setGiftFilter] = useState([]);
  const [excludedUsers, setExcludedUsers] = useState([]);
  const [excludedGroups, setExcludedGroups] = useState([]);
  const [excludedProducts, setExcludedProducts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [userAccessMap, setUserAccessMap] = useState(new Map());

  // Оптимизированная загрузка продуктов
  const loadProducts = useCallback(async () => {
    // Проверяем кэш
    const cachedProducts = productsCache.get('products');
    if (cachedProducts) {
      setProducts(cachedProducts);
      return cachedProducts;
    }

    try {
      // Загружаем курсы
      const coursesRef = collection(db, "courses");
      const coursesSnapshot = await getDocs(coursesRef);
      const courses = coursesSnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));

      // Загружаем тренинги
      const trainingsRef = collection(db, "trainings");
      const trainingsSnapshot = await getDocs(trainingsRef);
      const trainings = trainingsSnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));

      const productsData = { courses, trainings };
      
      // Сохраняем в кэш
      productsCache.set('products', productsData);
      setProducts(productsData);
      
      return productsData;
    } catch (error) {
      console.error('Ошибка загрузки продуктов:', error);
      if (showNotification) {
        showNotification('error', `Ошибка загрузки продуктов: ${error.message}`);
      }
      return { courses: [], trainings: [] };
    }
  }, [showNotification]);

  // Оптимизированная загрузка подарков
  const loadGifts = useCallback(async () => {
    // Проверяем кэш
    const cachedGifts = giftsCache.get('gifts');
    if (cachedGifts) {
      setGifts(cachedGifts);
      return cachedGifts;
    }

    try {
      const giftsRef = collection(db, "gifts");
      const giftsSnapshot = await getDocs(giftsRef);
      const giftsList = giftsSnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
      
      // Сохраняем в кэш
      giftsCache.set('gifts', giftsList);
      setGifts(giftsList);
      
      return giftsList;
    } catch (error) {
      console.error('Ошибка загрузки подарков:', error);
      if (showNotification) {
        showNotification('error', `Ошибка загрузки подарков: ${error.message}`);
      }
      return [];
    }
  }, [showNotification]);

  // Загрузка доступа пользователей к продуктам (оптимизированная)
  const loadUserAccess = useCallback(async (users) => {
    if (!users || users.length === 0) return;
    
    const newMap = new Map();
    
    // Используем существующие данные из пользователей
    users.forEach(user => {
      const userCourses = [...(user.courses || []), ...(user.coursesAccess || []).map(a => a.id || a)];
      const userTrainings = [...(user.trainings || []), ...(user.trainingsAccess || []).map(a => a.id || a)];
      
      newMap.set(user.id, {
        courses: userCourses,
        trainings: userTrainings,
        gifts: user.gifts || []
      });
    });
    
    setUserAccessMap(newMap);
  }, []);

  // Добавление/удаление исключений
  const addExcludedUser = useCallback((userId) => {
    setExcludedUsers(prev => [...new Set([...prev, userId])]);
  }, []);

  const removeExcludedUser = useCallback((userId) => {
    setExcludedUsers(prev => prev.filter(id => id !== userId));
  }, []);

  const addExcludedGroup = useCallback((group) => {
    setExcludedGroups(prev => [...new Set([...prev, group])]);
  }, []);

  const removeExcludedGroup = useCallback((group) => {
    setExcludedGroups(prev => prev.filter(g => g !== group));
  }, []);

  const addExcludedProduct = useCallback((productKey) => {
    setExcludedProducts(prev => [...new Set([...prev, productKey])]);
  }, []);

  const removeExcludedProduct = useCallback((productKey) => {
    setExcludedProducts(prev => prev.filter(p => p !== productKey));
  }, []);

  // Инициализация
  useEffect(() => {
    loadProducts();
    loadGifts();
  }, [loadProducts, loadGifts]);

  return {
    products,
    productFilter,
    setProductFilter,
    gifts,
    giftFilter,
    setGiftFilter,
    excludedUsers,
    excludedGroups,
    excludedProducts,
    addExcludedUser,
    removeExcludedUser,
    addExcludedGroup,
    removeExcludedGroup,
    addExcludedProduct,
    removeExcludedProduct,
    loadUserAccess,
    userAccessMap,
    loading
  };
};